'use client'

import { lowerQuote, useMarks } from '@/lib/quotes/hooks'

import React, { FC, ReactElement } from 'react'
import { CommandEmpty, CommandGroup, CommandItem, CommandLoading } from '@/components/ui/command'
import InscriptionContent from '@/components/inscription-content'
import useDebounce from '@/utils/hooks/useDebounce'
import useSwr from 'swr'
import { API_URL, SEARCH_VERSION, NETWORK } from '@/utils/constants'
import useSearchCollections from '@/utils/hooks/useSearchCollections'
import { useRouter } from 'next/navigation'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import Loading from '@/components/circle-loading'
import type { Command } from '../palette'
import axios from 'axios'
import isInscriptionId from '@/utils/is-inscription-id'
import isBtcAddress from '@/utils/is-btc-address'
import useBrc20Balance from '@/utils/hooks/useBrc20Balance'
import formatNumber from '@/utils/format-number'
import { useLogout } from '@/utils/hooks/useLoggedIn'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import useTokenPrices from '@/utils/hooks/useTokenPrices'
import isRune from '@/utils/is-rune'
import isAlkanes from '@/utils/is-alkanes'
import Link from 'next/link'
import BitcoinOrangeIcon from '@/icons/bitcoin-filled'

import CollectionDetail from './collection-detail'
import CollectionItem from '../components/collection-item'
import { ASSETS, assetSlug } from '@/app/markets/_assets'
import AssetIcon from '@/app/markets/_components/asset-icon'

import ConnectWallet from '../commands/connect-wallet'
import Wallet from '../commands/wallet'
import Inscribe from '../commands/inscribe'
import Market from '../commands/market'
import SwitchWallet from '../commands/switch-wallet'
import Runes from '../commands/runes'
import CreatorDashboard from '../commands/creator-dashboard'
import SignOut from '../commands/sign-out'
import ImportListingsCmd from '../commands/import-listings'
import ManageUtxosCmd from '../commands/manage-utxos'
import CollectionIcon from '@/components/collection-icon'
import { collectionObjectPagePath, parseCollectionObjectSlug } from '@/utils/collection-object'

import useWallet from '@/utils/hooks/useWallet'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'
import { cn } from '@/lib/utils'
import {
	addRecentSearch,
	clearRecentSearches,
	getRecentSearches,
	toRecentCollection,
	type RecentSearch
} from '@/utils/recent-searches'

const formatSatoshis = (satoshis: number) => {
	if (!satoshis) return '0'
	const btc = satoshis / 100_000_000
	return btc >= 0.01 ? `${btc.toFixed(2)} BTC` : `${satoshis.toLocaleString()} sats`
}

export const COMMANDS = {
	'connect-wallet': ConnectWallet,
	wallet: Wallet,
	market: Market,
	inscribe: Inscribe,
	runes: Runes,
	'creator-dashboard': CreatorDashboard,
	'sign-out': SignOut,
	'import-listings': ImportListingsCmd,
	'manage-utxos': ManageUtxosCmd
}

const _LIMIT = 16

const fetcher = async ([_url, search]: [string, string]) => {
	if (isInscriptionId(search)) {
		const { data } = await axios.get(`${API_URL}/inscription/${search}`, { timeout: 8000 })
		return { query: search, inscriptions: [data] }
	}

	const { data } = await axios.get(
		`${API_URL}/${SEARCH_VERSION}/${encodeURIComponent(
			search
		)}?limit=16&t=1&include_collection_objects=true&group_collection_objects=true`,
		{ timeout: 8000 }
	)
	return { ...data, query: search }
}

/**
 * The API ranks search hits by every venue's market and sinks lookalike
 * runes (`_search_weight`, `lookalike_of`); keep its order. Volume is the
 * fallback only for rows the API did not rank (local holdings).
 */
const searchRank = (item: any) => {
	const w = Number(item?._search_weight)
	return Number.isFinite(w) && w > 0 ? w : collectionSearchVolume(item)
}

const matchesQuery = (item: any, query: string) =>
	Boolean(query) && [item?.name, item?.slug].some((text) => text?.toLowerCase().includes(query))

const getSatoshiPriceUSD = (satoshis: number, rate: number) => {
	if (!satoshis || !rate) return '0.00'
	return ((satoshis * rate) / 100_000_000).toFixed(2)
}

const formatUSD = (value: string) => {
	const numValue = parseFloat(value)
	return numValue.toLocaleString('en-US', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2
	})
}

const formatBTCBalance = (satoshis: number) => {
	if (!satoshis) return '0'
	return (satoshis / 100_000_000).toFixed(6)
}

const collectionObjectType = (item: any) => item?.collection_object_type || item?.type
const isParentCollectionObject = (item: any) => collectionObjectType(item) === 'parent'
const isGalleryCollectionObject = (item: any) => collectionObjectType(item) === 'gallery'
const isCollectionObject = (item: any) =>
	isParentCollectionObject(item) || isGalleryCollectionObject(item)
const positiveNumber = (...values: any[]) => {
	for (const value of values) {
		const parsed = Number(value)
		if (Number.isFinite(parsed) && parsed > 0) return parsed
	}
	return 0
}
const collectionSearchVolume = (item: any) => {
	return positiveNumber(
		item?.global_volume_all_time,
		item?.stats?.global_volume_all_time,
		item?.volume_all_time,
		item?.volume_total,
		item?.total_volume,
		item?.stats?.volume_all_time,
		item?.stats?.volume_total
	)
}

const splitSearchCollections = (data: any) => {
	const mixed = Array.isArray(data?.collections) ? data.collections : []
	const collections = mixed.filter((item: any) => !isCollectionObject(item))
	const parents = Array.isArray(data?.parents)
		? data.parents
		: mixed.filter((item: any) => isParentCollectionObject(item))
	const galleries = Array.isArray(data?.galleries)
		? data.galleries
		: mixed.filter((item: any) => isGalleryCollectionObject(item))

	return { collections, parents, galleries }
}

const collectionObjectBadge = (item: any) => {
	if (isParentCollectionObject(item)) return 'Parent'
	if (isGalleryCollectionObject(item)) return 'Gallery'
	return undefined
}

const collectionObjectDescription = (item: any) => {
	const supply = Number(item?.total_supply || 0)
	if (!supply) return ''
	if (isParentCollectionObject(item)) return `${formatNumber(supply)} children`
	if (isGalleryCollectionObject(item)) return `${formatNumber(supply)} items`
	return ''
}

// The inscription id is the on-chain identity of a gallery/parent — its "contract
// address". Pull it from the explicit field, falling back to the id encoded in the slug.
const objectInscriptionId = (item: any): string => {
	if (isGalleryCollectionObject(item)) {
		return item?.gallery_inscription_id || parseCollectionObjectSlug(item?.slug).id || ''
	}
	if (isParentCollectionObject(item)) {
		return item?.parent_inscription_id || parseCollectionObjectSlug(item?.slug).id || ''
	}
	return ''
}

const truncateInscriptionId = (id: string) =>
	id.length > 16 ? `${id.slice(0, 6)}…${id.slice(-7)}` : id

// Stable identity for a result row, used for both dedup and React keys.
const objectDedupKey = (item: any) =>
	`${collectionObjectType(item) || 'collection'}:${
		objectInscriptionId(item) || item?.slug || item?.id || ''
	}`

// Drop exact duplicates (same slug or same inscription) within a single list.
const dedupObjects = (list: any[]) => {
	const seen = new Set<string>()
	const out: any[] = []
	for (const c of list) {
		const slugKey = c?.slug || c?.id || ''
		const inscriptionKey = objectInscriptionId(c)
		if ((slugKey && seen.has(slugKey)) || (inscriptionKey && seen.has(inscriptionKey))) continue
		if (slugKey) seen.add(slugKey)
		if (inscriptionKey) seen.add(inscriptionKey)
		out.push(c)
	}
	return out
}

const Suggestions: FC<{
	value: string
	onClose: () => void
	onNavigate: (v: string) => void
	onAddPage: (p: Command) => void
}> = (props): ReactElement => {
	const { value, onClose, onAddPage, onNavigate } = props
	const router = useRouter()
	const loggedIn = useLoggedIn()
	const logout = useLogout()
	const { exchangeRate } = useExchangeRate()

	const trimmedValue = value.trim()
	const query = trimmedValue.toLowerCase()
	const isAddressQuery = isBtcAddress(trimmedValue)
	// Local matches use every keystroke; only the full remote search is debounced.
	const debouncedValue = useDebounce(query, 120)
	const [objectTab, setObjectTab] = React.useState('all')
	const { data, error, isLoading, isValidating } = useSwr(
		debouncedValue && !isAddressQuery ? ['/search', debouncedValue] : null,
		fetcher,
		{
			keepPreviousData: true,
			dedupingInterval: 30_000,
			revalidateOnFocus: false,
			shouldRetryOnError: false
		}
	)

	// Recently opened collections, newest first; re-read whenever one is added.
	const [recents, setRecents] = React.useState<RecentSearch[]>([])
	React.useEffect(() => {
		const read = () => setRecents(getRecentSearches())
		read()
		window.addEventListener('ow:recent-searches', read)
		return () => window.removeEventListener('ow:recent-searches', read)
	}, [])
	const { data: brc20Data } = useBrc20Balance()
	const { data: tokenPrices } = useTokenPrices()
	const { data: inscriptionData } = useWallet({ cache: true })
	const searchSections = React.useMemo(() => {
		const sections = splitSearchCollections(data)
		if (data?.query === query) return sections
		// A late response may contribute matching rows, never unrelated old results.
		return {
			collections: sections.collections.filter((item: any) => matchesQuery(item, query)),
			parents: sections.parents.filter((item: any) => matchesQuery(item, query)),
			galleries: sections.galleries.filter((item: any) => matchesQuery(item, query))
		}
	}, [data, query])
	const { data: collectionsData } = useSearchCollections()

	// Create a map of slug -> volume_all_time for quick lookup
	const volumeMap = React.useMemo(() => {
		const map: Record<string, number> = {}
		if (collectionsData?.collections && Array.isArray(collectionsData.collections)) {
			for (const c of collectionsData.collections) {
				if (c.slug) {
					map[c.slug] = collectionSearchVolume(c)
				}
			}
		}
		return map
	}, [collectionsData])

	const heldTokenSlugs = React.useMemo(
		() => (brc20Data || []).map((c: any) => c?.collection?.slug).filter(Boolean) as string[],
		[brc20Data]
	)
	const marks = useMarks(heldTokenSlugs)
	const topCoins = React.useMemo(() => {
		const btcBalance = {
			ticker: `${NETWORK} Balance`,
			collection: { name: 'Bitcoin', slug: 'bitcoin', volume_all_time: Infinity },
			overall_balance: formatBTCBalance(inscriptionData?.balance || 0),
			balanceUSD: getSatoshiPriceUSD(inscriptionData?.balance || 0, exchangeRate),
			satoshiPrice: inscriptionData?.balance || 0,
			tickerIcon: <BitcoinOrangeIcon className="!w-[32px] !h-[32px] min-w-[32px] min-h-[32px]" />
		}

		const tokens = (brc20Data || [])
			?.map((c) => {
				let satoshiPrice = 0
				let balanceUSD = '0'
				const collection = (tokenPrices || []).find((e) => e.slug === c?.collection?.slug)

				// Worth: fair price or floor, whichever is lower (per token).
				const unit =
					lowerQuote(marks[c?.collection?.slug]?.fair_sats, Number(collection?.floor_price_per) || null) || 0
				if (unit && c?.overall_balance) {
					satoshiPrice = unit * c.overall_balance
					balanceUSD = getSatoshiPriceUSD(satoshiPrice, exchangeRate)
				}
				if (
					unit &&
					(isRune(collection?.slug) || isAlkanes(collection?.slug))
				) {
					satoshiPrice = unit * c.amount
					balanceUSD = getSatoshiPriceUSD(satoshiPrice, exchangeRate)
				}

				// Get volume from the volumeMap
				const volume = volumeMap[c?.collection?.slug] || 0

				return { ...c, balanceUSD, satoshiPrice, _volume: volume }
			})
			// Sort by volume (highest first)
			?.sort((a, b) => (b._volume || 0) - (a._volume || 0))
			?.slice(0, 2)

		return [btcBalance, ...tokens]
	}, [brc20Data, tokenPrices, exchangeRate, inscriptionData, volumeMap, marks])

	const handleSelectSuggestion = React.useCallback(
		(e: any) => {
			if (e.onClick) {
				return e.onClick({ router, onClose, logout })
			}

			onAddPage(e)
		},
		[onAddPage, router, onClose, logout]
	)

	const renderSuggestion = React.useCallback(
		(e: any) => {
			return (
				<CommandItem key={e.name} onSelect={() => handleSelectSuggestion(e)}>
					<e.icon className="mr-2 size-4" />
					<span>{e.name}</span>
				</CommandItem>
			)
		},
		[handleSelectSuggestion]
	)

	const tokenDataMap = React.useMemo(() => {
		const map = {}
		if (!brc20Data || !Array.isArray(brc20Data)) {
			return map
		}

		for (let e of brc20Data) {
			;(map as any)[e?.collection?.slug] = e
		}

		return map
	}, [brc20Data])

	const inscriptionDataMap = React.useMemo(() => {
		const map = {}
		if (!inscriptionData || !Array.isArray(inscriptionData.inscriptions)) {
			return map
		}

		for (let e of inscriptionData.inscriptions) {
			const key = e?.collection?.slug || 'uncategorized'
			;(map as any)[key] = ((map as any)[key] || 0) + 1
			;(map as any)['frozen'] = ((map as any)['frozen'] || 0) + 1
		}

		return map
	}, [inscriptionData])

	const renderCollection = React.useCallback(
		(c: any, _i: any) => {
			const token = (tokenDataMap as any)[c.slug]
			const inscriptions = (inscriptionDataMap as any)[c.slug]
			const hasOwnedBalance =
				(token && Number(token.overall_balance || 0) > 0) || Number(inscriptions || 0) > 0
			const balanceLabel = token
				? token.overall_balance > 0
					? `Balance: ${formatNumber(token.overall_balance)}`
					: ''
				: inscriptions > 0
				? `Balance: ${formatNumber(inscriptions)}`
				: ''

			// Secondary line priority: your balance > on-chain id (galleries/parents) > supply.
			const inscriptionId = objectInscriptionId(c)
			const secondary =
				(c.lookalike_of?.name ? `Imitates ${c.lookalike_of.name}` : '') ||
				balanceLabel ||
				(inscriptionId ? truncateInscriptionId(inscriptionId) : '') ||
				collectionObjectDescription(c)
			const secondaryMono = !balanceLabel && Boolean(inscriptionId)

			// Only show stats when we have real data (not 0 or missing). Search returns
			// owners as null, so the second column is supply (present on every object).
			const volume = (volumeMap as any)[c.slug] || collectionSearchVolume(c)
			const supply = Number(c.total_supply || 0)

			return (
				<CollectionItem
					key={objectDedupKey(c)}
					collection={c}
					tag={c.lookalike_of ? 'Lookalike' : collectionObjectBadge(c)}
					// Anyone can inscribe a gallery, so the badge is reserved for real
					// marketplace collections — galleries/parents show their inscription id instead.
					verified={!isCollectionObject(c) && Boolean(c.verified)}
					verifiedInscriptionId={c.gallery_inscription_id}
					secondary={secondary}
					secondaryMono={secondaryMono}
					primaryStat={volume > 0 ? formatSatoshis(volume) : ''}
					secondaryStat={supply > 0 ? formatNumber(supply) : ''}
					onSelect={() => {
						addRecentSearch({ slug: c.slug, name: c.name, collection: toRecentCollection(c) })
						if (hasOwnedBalance) {
							const isVirtual = c.slug === 'frozen' || c.slug === 'uncategorized'
							onAddPage({
								featured: false,
								component: CollectionDetail,
								name: c.name,
								args: { slug: c.slug },
								heading: isVirtual ? '' : 'Collection'
							})
							if (!isVirtual) {
								router.push(collectionObjectPagePath(c.slug))
							}
						} else {
							onNavigate(collectionObjectPagePath(c.slug))
						}
					}}
				/>
			)
		},
		[tokenDataMap, inscriptionDataMap, onAddPage, router, onNavigate, volumeMap]
	)

	const renderInscription = React.useCallback(
		(c: any, _i: any) => {
			return (
				<CommandItem value={c.id} key={c.id} onSelect={() => onNavigate(`/inscription/${c.id}`)}>
					<InscriptionContent inscription={c} className="mr-2 size-4" />
					<div className="flex items-center justify-between w-full">
						<span>Inscription #{c.num}</span>
						{c.escrow?.satoshi_price && (
							<span className="text-xs text-muted-foreground">
								{formatSatoshis(c.escrow.satoshi_price)}
							</span>
						)}
					</div>
				</CommandItem>
			)
		},
		[onNavigate]
	)

	const suggestions: Command[] = React.useMemo(() => {
		// @ts-ignore
		const options: Command[] = Object.values(COMMANDS).filter((e) => e.filter({ loggedIn }))

		let res

		if (value) {
			res = options.filter((e) => e.name.toLowerCase().includes(value.toLowerCase()))
		} else {
			res = options.filter((e) => e.featured)
		}

		return res
	}, [loggedIn, value])
	const inscriptions = inscriptionData?.inscriptions

	// Get user's owned collections from brc20Data and inscriptionData for local search
	const ownedCollections = React.useMemo(() => {
		const collections: any[] = []
		const seenSlugs = new Set<string>()

		// Add collections from token balances
		if (brc20Data && Array.isArray(brc20Data)) {
			for (const token of brc20Data) {
				const tokenCollection = token?.collection
				const slug = tokenCollection?.slug
				if (slug && !seenSlugs.has(slug)) {
					seenSlugs.add(slug)
					collections.push({
						...tokenCollection,
						// Use collection name, or fall back to ticker
						name: tokenCollection.name || token.ticker || slug,
						volume_all_time: tokenCollection.volume_all_time,
						owners: tokenCollection.owners,
						_isLocalOnly: !tokenCollection.volume_all_time // Flag to know if we have full data
					})
				}
			}
		}

		// Add collections from inscriptions
		if (inscriptions && Array.isArray(inscriptions)) {
			for (const inscription of inscriptions) {
				const inscCollection = inscription?.collection
				const slug = inscCollection?.slug
				if (slug && !seenSlugs.has(slug)) {
					seenSlugs.add(slug)
					collections.push({
						...inscCollection,
						name: inscCollection.name || slug,
						volume_all_time: inscCollection.volume_all_time,
						owners: inscCollection.owners,
						_isLocalOnly: !inscCollection.volume_all_time
					})
				}
			}
		}

		// Add synthetic collection for inscriptions without a collection
		if ((inscriptionDataMap as any)['uncategorized'] > 0) {
			collections.push({
				name: 'Uncategorized',
				slug: 'uncategorized',
				id: 'uncategorized'
			})
		}

		// Add synthetic collection for all inscriptions with excess sats (>546)
		if ((inscriptionDataMap as any)['frozen'] > 0) {
			collections.push({
				name: 'Manage Inscriptions',
				slug: 'frozen',
				id: 'frozen'
			})
		}

		return collections
	}, [brc20Data, inscriptions, inscriptionDataMap])

	const collections = React.useMemo(() => {
		const apiCollections = query ? searchSections.collections : []
		const localMatches = [
			...ownedCollections,
			...(Array.isArray(collectionsData?.collections) ? collectionsData.collections : [])
		].filter((item) => matchesQuery(item, query))
		// API rows refresh metadata; local rows remain usable while the request runs.
		const allCollections = dedupObjects([...apiCollections, ...localMatches])

		if (!allCollections.length) {
			return []
		}

		// Split into balance collections (user owns) and non-balance collections
		const balanceCollections = allCollections
			.filter((e) => (tokenDataMap as any)[e.slug] || (inscriptionDataMap as any)[e.slug])
			.sort((a, b) => searchRank(b) - searchRank(a))

		const nonBalanceCollections = allCollections
			.filter((e) => !balanceCollections.find((a) => a.slug == e.slug))
			.sort((a, b) => searchRank(b) - searchRank(a))

		return balanceCollections.concat(nonBalanceCollections)
	}, [
		tokenDataMap,
		inscriptionDataMap,
		searchSections.collections,
		ownedCollections,
		query,
		collectionsData,
		volumeMap
	])

	const parentCollections = React.useMemo(
		() =>
			[...searchSections.parents]
				.sort((a: any, b: any) => collectionSearchVolume(b) - collectionSearchVolume(a))
				.slice(0, _LIMIT),
		[searchSections.parents]
	)
	const galleryCollections = React.useMemo(
		() =>
			[...searchSections.galleries]
				.sort((a: any, b: any) => collectionSearchVolume(b) - collectionSearchVolume(a))
				.slice(0, _LIMIT),
		[searchSections.galleries]
	)

	// One unified, ranked, de-duplicated result list (Hyperliquid-style table).
	// Merging collections + parents + galleries removes cross-section duplicates,
	// keeps the user's own holdings on top, then ranks everything else by volume.
	const objectResults = React.useMemo(() => {
		// A marketplace collection and a like-named gallery/parent mirror (the
		// "Bitcoin Punks" collection vs its gallery) are the duplicate users see.
		// Drop the mirror; leave distinct same-named collections alone.
		const merged = [...collections, ...parentCollections, ...galleryCollections]
		const seen = new Set<string>()
		const collectionNames = new Set<string>(
			collections
				.map((c: any) => c?.name?.trim().toLowerCase())
				.filter((n: string): n is string => Boolean(n))
		)
		const deduped: any[] = []
		for (const c of merged) {
			const slugKey = c?.slug || c?.id || ''
			const inscriptionKey = objectInscriptionId(c)
			if ((slugKey && seen.has(slugKey)) || (inscriptionKey && seen.has(inscriptionKey))) {
				continue
			}
			if (isCollectionObject(c)) {
				const name = c?.name?.trim().toLowerCase()
				if (name && collectionNames.has(name)) continue
			}
			if (slugKey) seen.add(slugKey)
			if (inscriptionKey) seen.add(inscriptionKey)
			deduped.push(c)
		}

		const isOwned = (c: any) =>
			Boolean((tokenDataMap as any)[c.slug] || (inscriptionDataMap as any)[c.slug])

		const owned = deduped.filter(isOwned)
		const rest = deduped.filter((c) => !isOwned(c)).sort((a, b) => searchRank(b) - searchRank(a))

		return [...owned, ...rest].slice(0, _LIMIT * 3)
	}, [
		collections,
		parentCollections,
		galleryCollections,
		tokenDataMap,
		inscriptionDataMap,
		volumeMap
	])

	// Type tabs (All / Collections / Galleries / Parents), shown only when more than
	// one object type is present. The type tabs are pure filters — the Galleries tab
	// shows every gallery, including the mirrors the "All" view de-duplicates away.
	const availableTabs = React.useMemo(() => {
		const tabs: { key: string; label: string }[] = [{ key: 'all', label: 'All' }]
		if (collections.length) tabs.push({ key: 'collections', label: 'Collections' })
		if (galleryCollections.length) tabs.push({ key: 'galleries', label: 'Galleries' })
		if (parentCollections.length) tabs.push({ key: 'parents', label: 'Parents' })
		return tabs
	}, [collections.length, galleryCollections.length, parentCollections.length])

	// Fall back to "all" in render if the active tab's type disappeared on a new query.
	const activeTab = availableTabs.some((t) => t.key === objectTab) ? objectTab : 'all'
	const showTabs = availableTabs.length > 2

	const displayedResults = React.useMemo(() => {
		if (activeTab === 'collections') return dedupObjects(collections)
		if (activeTab === 'galleries') return dedupObjects(galleryCollections)
		if (activeTab === 'parents') return dedupObjects(parentCollections)
		return objectResults
	}, [activeTab, collections, galleryCollections, parentCollections, objectResults])

	const isDebouncePending = query !== debouncedValue
	const isRefreshing =
		Boolean(query) && !isAddressQuery && (isLoading || isValidating || isDebouncePending)
	const currentInscriptions = data?.query === query ? data?.inscriptions : []
	// SWR isLoading is true even with previous data. Never hide usable matches.
	const isSearching = isRefreshing && !displayedResults.length && !currentInscriptions?.length
	const searchFailed = error && !isDebouncePending && !isAddressQuery && Boolean(query)

	const recentMatches = React.useMemo(() => {
		const q = trimmedValue.toLowerCase()
		const list = q
			? recents.filter((r) => r.name?.toLowerCase().includes(q) || r.slug.toLowerCase().includes(q))
			: recents
		return list.slice(0, q ? 3 : 5)
	}, [recents, trimmedValue])

	const openCollection = React.useCallback(
		(slug: string, name: string) => {
			const owned =
				Number((tokenDataMap as any)[slug]?.overall_balance || 0) > 0 ||
				Number((inscriptionDataMap as any)[slug] || 0) > 0
			if (owned) {
				onAddPage({
					featured: false,
					component: CollectionDetail,
					name,
					args: { slug },
					heading: 'Collection'
				})
				router.push(collectionObjectPagePath(slug))
			} else {
				onNavigate(collectionObjectPagePath(slug))
			}
		},
		[tokenDataMap, inscriptionDataMap, onAddPage, onNavigate, router]
	)

	// Surface /markets/{asset} pages at the top of the result list when the
	// query matches a supported asset by name or ticker. So "bitcoin", "btc",
	// "ethereum", "eth", "solana", "sol" all land the user on our MoonPay-
	// powered buy/sell flow before any collection search results.
	const marketMatches = React.useMemo(() => {
		const q = trimmedValue.toLowerCase()
		if (!q) return []
		return ASSETS.filter(
			(a) => a.name.toLowerCase().includes(q) || a.symbol.toLowerCase().includes(q)
		)
	}, [trimmedValue])

	return (
		<>
			{recentMatches.length > 0 && (
				<CommandGroup
					heading={
						<div className="flex w-full items-center justify-between">
							<span>Recent</span>
							{!trimmedValue && (
								<span
									onClick={clearRecentSearches}
									className="cursor-pointer text-[10px] normal-case tracking-normal text-muted-foreground hover:text-foreground hover:underline"
									role="button"
									tabIndex={0}
									onKeyDown={onKeyboardClick(clearRecentSearches)}
								>
									Clear
								</span>
							)}
						</div>
					}
				>
					{recentMatches.map((r) => (
						<CommandItem
							key={`recent:${r.slug}`}
							value={`recent:${r.slug}`}
							onSelect={() => {
								addRecentSearch({ slug: r.slug, name: r.name, collection: r.collection })
								openCollection(r.slug, r.name)
							}}
						>
							<CollectionIcon
								collection={r.collection || { slug: r.slug, name: r.name, icon: r.icon }}
								className="mr-2 size-8 shrink-0 rounded-full overflow-hidden"
							/>
							<div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
								<span className="truncate text-sm">{r.name}</span>
								<span className="text-xxs text-muted-foreground">Recently viewed</span>
							</div>
						</CommandItem>
					))}
				</CommandGroup>
			)}
			{marketMatches.length > 0 && (
				<CommandGroup heading="Markets">
					{marketMatches.map((asset) => (
						<CommandItem
							key={asset.symbol}
							value={`market:${asset.symbol}`}
							onSelect={() => onNavigate(`/markets/${assetSlug(asset)}`)}
							className="flex items-center gap-2"
						>
							<AssetIcon asset={asset} size={20} className="size-5 shrink-0" />
							<div className="flex flex-col gap-0.5">
								<span className="text-sm leading-tight">
									{asset.name} <span className="text-xs text-muted-foreground">{asset.symbol}</span>
								</span>
								<span className="text-[10px] leading-none text-muted-foreground">
									Buy or sell with card, Apple Pay and more
								</span>
							</div>
						</CommandItem>
					))}
				</CommandGroup>
			)}
			{isAddressQuery && (
				<CommandGroup heading="Address">
					<CommandItem
						value={`address:${trimmedValue}`}
						onSelect={() => onNavigate(`/address/${trimmedValue}`)}
						className="flex items-center gap-2"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
							className="mr-1 size-4 text-muted-foreground"
						>
							<rect x="3" y="4" width="18" height="16" rx="2" />
							<path d="M7 9h10M7 13h6" />
						</svg>
						<div className="flex flex-col">
							<span className="font-mono text-sm leading-none">
								{trimmedValue.length > 22
									? `${trimmedValue.slice(0, 10)}…${trimmedValue.slice(-8)}`
									: trimmedValue}
							</span>
							<span className="mt-1 text-[10px] text-muted-foreground">
								View public wallet page
							</span>
						</div>
					</CommandItem>
				</CommandGroup>
			)}
			{suggestions.length > 0 && (
				<CommandGroup heading="Suggestions">{suggestions.map(renderSuggestion)}</CommandGroup>
			)}
			{!isRefreshing && !isAddressQuery && !searchFailed && (
				<CommandEmpty>No results found.</CommandEmpty>
			)}
			{searchFailed && (
				<p role="status" className="px-4 py-3 text-xs text-muted-foreground">
					Full search is temporarily unavailable. Try again in a moment.
				</p>
			)}
			{isSearching && (
				<CommandLoading className="">
					<div className="flex items-center justify-center h-[168px]">
						<Loading />
					</div>
				</CommandLoading>
			)}
			{!isSearching && showTabs && (
				<div className="flex items-center gap-4 border-b border-border px-3 text-xs">
					{availableTabs.map((t) => (
						<button
							key={t.key}
							type="button"
							// Keep focus in the input so arrow-key navigation keeps working.
							onMouseDown={(e) => e.preventDefault()}
							onClick={() => setObjectTab(t.key)}
							className={cn(
								'-mb-px border-b-2 pb-2 pt-1 transition-colors',
								activeTab === t.key
									? 'border-foreground text-foreground'
									: 'border-transparent text-muted-foreground hover:text-foreground'
							)}
						>
							{t.label}
						</button>
					))}
				</div>
			)}
			{!isSearching && displayedResults.length > 0 && (
				<CommandGroup
					aria-busy={isRefreshing}
					heading={
						<div className="flex w-full items-center text-[10px] text-muted-foreground">
							<span className="w-10 shrink-0" aria-hidden />
							<span className="min-w-0 flex-1">Name</span>
							<span className="ml-3 w-24 shrink-0 text-right">Volume</span>
							<span className="ml-2 w-16 shrink-0 text-right">Supply</span>
						</div>
					}
				>
					{displayedResults.map(renderCollection)}
				</CommandGroup>
			)}
			{!isSearching && currentInscriptions?.length > 0 && (
				<CommandGroup heading="Inscription">
					{currentInscriptions?.map(renderInscription)}
				</CommandGroup>
			)}
			{!isSearching &&
				topCoins?.length > 0 &&
				loggedIn &&
				inscriptionData?.balance > 0 &&
				!value && (
					<CommandGroup
						heading={
							<div className="flex justify-between items-center w-full">
								<Link href="/wallet" className="hover:underline">
									My Wallet
								</Link>
								<span
									onClick={() => handleSelectSuggestion(SwitchWallet)}
									className="text-xs text-muted-foreground hover:underline cursor-pointer flex items-center gap-1"
									role="button"
									tabIndex={0}
									onKeyDown={onKeyboardClick(() => handleSelectSuggestion(SwitchWallet))}
								>
									<SwitchWallet.icon className="size-3" />
									Switch Wallet
								</span>
							</div>
						}
					>
						{topCoins.map((coin) => (
							<CommandItem
								key={coin.collection?.slug}
								onSelect={() => {
									if (coin.ticker.includes('Balance')) {
										onNavigate('/wallet')
									} else {
										onAddPage({
											featured: false,
											component: CollectionDetail,
											name: coin.collection?.name,
											args: { slug: coin.collection?.slug },
											heading: 'Collection'
										})
									}
								}}
							>
								<div className="flex items-center justify-between w-full py-0">
									<div className="flex items-center gap-1.5">
										{coin.tickerIcon ? (
											coin.tickerIcon
										) : (
											<CollectionIcon
												collection={coin.collection}
												className="size-8 rounded-full overflow-hidden [&>div]:hidden"
												imageClassName="size-8 rounded-full object-cover"
											/>
										)}
										<span className="text-xs">{coin.ticker}</span>
									</div>
									<div className="flex flex-col items-end text-xs">
										<span>{formatNumber(coin.overall_balance)}</span>
										<span className="text-muted-foreground text-[10px]">
											${formatUSD(coin.balanceUSD)}
										</span>
									</div>
								</div>
							</CommandItem>
						))}
						<CommandItem
							onSelect={() => onNavigate('/wallet')}
							className="hover:bg-transparent ![&:hover]:bg-transparent"
						>
							<div className="w-full text-xs text-muted-foreground hover:text-foreground text-center">
								View All Assets
							</div>
						</CommandItem>
					</CommandGroup>
				)}
		</>
	)
}

export default Suggestions
